/**
 * PromotionEditorModal
 *
 * Modal for creating and editing promotions with discount details,
 * date ranges, and content generation guidelines.
 */

import React, { useState, useEffect } from 'react';
import {
    X,
    Tag,
    Percent,
    DollarSign,
    Gift,
    Package,
    Truck,
    Calendar,
    AlertCircle,
    Loader2,
    Infinity,
    ShoppingBag,
    Check
} from 'lucide-react';
import {
    PROMOTION_TYPES,
    URGENCY_LEVELS,
    DEFAULT_PROMOTION
} from '../../services/promotionService';
import ConfirmationModal from '../ConfirmationModal';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

// Icons for promotion types
const PROMOTION_TYPE_ICONS = {
    percentage: Percent,
    fixed: DollarSign,
    bogo: Gift,
    bundle: Package,
    free_shipping: Truck
};

const PromotionEditorModal = ({
    isOpen,
    onClose,
    onSave,
    promotion = null,
    loading = false,
    products = []
}) => {
    const [formData, setFormData] = useState({ ...DEFAULT_PROMOTION });
    const [errors, setErrors] = useState({});

    const { captureInitialState, showUnsavedModal, setShowUnsavedModal, handleCloseAttempt } = useUnsavedChanges(formData);

    // Initialize form with promotion data or defaults
    useEffect(() => {
        let initialData;
        if (promotion) {
            initialData = {
                ...DEFAULT_PROMOTION,
                ...promotion,
                contentGuidelines: {
                    ...DEFAULT_PROMOTION.contentGuidelines,
                    ...promotion.contentGuidelines
                }
            };
        } else {
            // Set default dates for new promotion
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const nextWeek = new Date();
            nextWeek.setDate(nextWeek.getDate() + 7);

            initialData = {
                ...DEFAULT_PROMOTION,
                startDate: tomorrow.toISOString().split('T')[0],
                endDate: nextWeek.toISOString().split('T')[0]
            };
        }
        setFormData(initialData);
        captureInitialState(initialData);
        setErrors({});
    }, [promotion, isOpen]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error when field is modified
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleGuidelinesChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            contentGuidelines: {
                ...prev.contentGuidelines,
                [field]: value
            }
        }));
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name?.trim()) {
            newErrors.name = 'Promotion name is required';
        }

        if (!formData.discountValue?.trim() && formData.type !== 'free_shipping') {
            newErrors.discountValue = 'Discount value is required';
        }

        // Only validate dates if not ongoing
        if (!formData.isOngoing) {
            if (!formData.startDate) {
                newErrors.startDate = 'Start date is required';
            }

            if (!formData.endDate) {
                newErrors.endDate = 'End date is required';
            }

            if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
                newErrors.endDate = 'End date must be after start date';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAttemptClose = () => {
        if (!handleCloseAttempt()) {
            onClose();
        }
    };

    const handleSaveAndClose = async () => {
        if (!validate()) {
            setShowUnsavedModal(false);
            return;
        }
        await onSave(formData);
        setShowUnsavedModal(false);
    };

    const handleDiscardAndClose = () => {
        setShowUnsavedModal(false);
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        await onSave(formData);
    };

    if (!isOpen) return null;

    const isEditing = !!promotion?.id;

    return (
        <>
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: 'var(--space-4)'
            }}
            onClick={(e) => e.target === e.currentTarget && handleAttemptClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-4)',
                    paddingBottom: 'var(--space-3)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <Tag size={20} style={{ color: 'var(--color-primary)' }} />
                        <h3 style={{ margin: 0 }}>
                            {isEditing ? 'Edit Promotion' : 'Create Promotion'}
                        </h3>
                    </div>
                    <button
                        onClick={handleAttemptClose}
                        className="btn btn-ghost"
                        style={{ padding: 'var(--space-1)' }}
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Promotion Name */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-1)'
                        }}>
                            Promotion Name *
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="e.g., Summer Sale, Black Friday, Member Discount"
                            className={`input ${errors.name ? 'input-error' : ''}`}
                            style={{ width: '100%' }}
                        />
                        {errors.name && (
                            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--error-600)' }}>
                                {errors.name}
                            </span>
                        )}
                    </div>

                    {/* Promotion Type */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            Discount Type *
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                            {Object.entries(PROMOTION_TYPES).map(([type, info]) => {
                                const Icon = PROMOTION_TYPE_ICONS[type] || Tag;
                                const isSelected = formData.type === type;

                                return (
                                    <button
                                        key={type}
                                        type="button"
                                        onClick={() => handleChange('type', type)}
                                        className={`btn ${isSelected ? 'btn-primary' : 'btn-ghost'}`}
                                        style={{ flex: '1 1 auto', minWidth: '100px' }}
                                    >
                                        <Icon size={16} />
                                        {info.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Discount Value and Code */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Discount Value {formData.type !== 'free_shipping' && '*'}
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                {formData.type === 'percentage' && <span>%</span>}
                                {formData.type === 'fixed' && <span>$</span>}
                                <input
                                    type="text"
                                    value={formData.discountValue}
                                    onChange={(e) => handleChange('discountValue', e.target.value)}
                                    placeholder={PROMOTION_TYPES[formData.type]?.example || 'Enter value'}
                                    className={`input ${errors.discountValue ? 'input-error' : ''}`}
                                    style={{ flex: 1 }}
                                    disabled={formData.type === 'free_shipping'}
                                />
                            </div>
                            {errors.discountValue && (
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--error-600)' }}>
                                    {errors.discountValue}
                                </span>
                            )}
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Promo Code (optional)
                            </label>
                            <input
                                type="text"
                                value={formData.discountCode}
                                onChange={(e) => handleChange('discountCode', e.target.value.toUpperCase())}
                                placeholder="e.g., SUMMER20"
                                className="input"
                                style={{ width: '100%', fontFamily: 'monospace' }}
                            />
                        </div>
                    </div>

                    {/* Ongoing Toggle */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        marginBottom: 'var(--space-4)',
                        padding: 'var(--space-3)',
                        background: formData.isOngoing ? 'var(--success-50)' : 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-md)',
                        border: `1px solid ${formData.isOngoing ? 'var(--success-200)' : 'var(--color-border)'}`
                    }}>
                        <Infinity size={20} style={{ color: formData.isOngoing ? 'var(--success-600)' : 'var(--color-text-tertiary)' }} />
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            cursor: 'pointer',
                            flex: 1
                        }}>
                            <input
                                type="checkbox"
                                checked={formData.isOngoing || false}
                                onChange={(e) => handleChange('isOngoing', e.target.checked)}
                                style={{ width: '16px', height: '16px' }}
                            />
                            <div>
                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                                    Ongoing Promotion
                                </span>
                                <p style={{
                                    margin: 0,
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    No end date - applies to all content until disabled
                                </p>
                            </div>
                        </label>
                    </div>

                    {/* Date Range - only show if not ongoing */}
                    {!formData.isOngoing && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 'var(--space-4)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    Start Date *
                                </label>
                                <input
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) => handleChange('startDate', e.target.value)}
                                    className={`input ${errors.startDate ? 'input-error' : ''}`}
                                    style={{ width: '100%' }}
                                />
                                {errors.startDate && (
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--error-600)' }}>
                                        {errors.startDate}
                                    </span>
                                )}
                            </div>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    marginBottom: 'var(--space-1)'
                                }}>
                                    End Date *
                                </label>
                                <input
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) => handleChange('endDate', e.target.value)}
                                    min={formData.startDate}
                                    className={`input ${errors.endDate ? 'input-error' : ''}`}
                                    style={{ width: '100%' }}
                                />
                                {errors.endDate && (
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--error-600)' }}>
                                        {errors.endDate}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Description */}
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-1)'
                        }}>
                            Description (optional)
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="Describe the promotion for content generation (e.g., 'End of season clearance to make room for new arrivals')"
                            className="input"
                            style={{ width: '100%', minHeight: '80px', resize: 'vertical' }}
                        />
                    </div>

                    {/* Linked Products Section */}
                    {products.length > 0 && (
                        <div style={{
                            background: 'var(--color-bg-secondary)',
                            padding: 'var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-3)'
                            }}>
                                <ShoppingBag size={16} style={{ color: 'var(--color-primary)' }} />
                                <h4 style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
                                    Link Products to Feature
                                </h4>
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    (optional)
                                </span>
                            </div>
                            <p style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)',
                                margin: '0 0 var(--space-3)'
                            }}>
                                Select products that must be featured in content generated for this promotion.
                            </p>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--space-2)',
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}>
                                {products.filter(p => p.isActive !== false).map(product => {
                                    const isLinked = formData.linkedProducts?.includes(product.id);
                                    return (
                                        <label
                                            key={product.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-3)',
                                                padding: 'var(--space-2)',
                                                background: isLinked ? 'var(--color-primary-alpha)' : 'var(--color-bg-primary)',
                                                borderRadius: 'var(--radius-sm)',
                                                border: `1px solid ${isLinked ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                cursor: 'pointer',
                                                transition: 'all var(--transition-fast)'
                                            }}
                                        >
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: 'var(--radius-sm)',
                                                border: `2px solid ${isLinked ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                background: isLinked ? 'var(--color-primary)' : 'transparent',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0
                                            }}>
                                                {isLinked && <Check size={12} style={{ color: 'white' }} />}
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={isLinked}
                                                onChange={(e) => {
                                                    const newLinkedProducts = e.target.checked
                                                        ? [...(formData.linkedProducts || []), product.id]
                                                        : (formData.linkedProducts || []).filter(id => id !== product.id);
                                                    handleChange('linkedProducts', newLinkedProducts);
                                                }}
                                                style={{ display: 'none' }}
                                            />
                                            {product.imageUrl && (
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.name}
                                                    style={{
                                                        width: '40px',
                                                        height: '40px',
                                                        objectFit: 'cover',
                                                        borderRadius: 'var(--radius-sm)',
                                                        flexShrink: 0
                                                    }}
                                                />
                                            )}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontWeight: 500,
                                                    fontSize: 'var(--font-size-sm)',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {product.name}
                                                </div>
                                                {product.price && (
                                                    <div style={{
                                                        fontSize: 'var(--font-size-xs)',
                                                        color: 'var(--color-text-secondary)'
                                                    }}>
                                                        {product.currency || '$'}{product.price}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            {formData.linkedProducts?.length > 0 && (
                                <div style={{
                                    marginTop: 'var(--space-2)',
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-primary)',
                                    fontWeight: 500
                                }}>
                                    {formData.linkedProducts.length} product{formData.linkedProducts.length !== 1 ? 's' : ''} linked
                                </div>
                            )}
                        </div>
                    )}

                    {/* Content Guidelines Section */}
                    <div style={{
                        background: 'var(--color-bg-secondary)',
                        padding: 'var(--space-4)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-sm)' }}>
                            Content Generation Guidelines
                        </h4>

                        {/* Urgency Level */}
                        <div style={{ marginBottom: 'var(--space-3)' }}>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                Urgency Level
                            </label>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                {Object.entries(URGENCY_LEVELS).map(([level, info]) => (
                                    <button
                                        key={level}
                                        type="button"
                                        onClick={() => handleGuidelinesChange('urgencyLevel', level)}
                                        className={`btn btn-sm ${formData.contentGuidelines.urgencyLevel === level ? 'btn-primary' : 'btn-ghost'}`}
                                        style={{ flex: 1 }}
                                        title={info.description}
                                    >
                                        {info.label}
                                    </button>
                                ))}
                            </div>
                            <p style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)',
                                margin: 'var(--space-1) 0 0'
                            }}>
                                {URGENCY_LEVELS[formData.contentGuidelines.urgencyLevel]?.description}
                            </p>
                        </div>

                        {/* Emphasize Scarcity */}
                        <div style={{ marginBottom: 'var(--space-3)' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={formData.contentGuidelines.emphasizeScarcity}
                                    onChange={(e) => handleGuidelinesChange('emphasizeScarcity', e.target.checked)}
                                    style={{ width: '16px', height: '16px' }}
                                />
                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                                    Emphasize Scarcity
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                                    (Limited stock, while supplies last)
                                </span>
                            </label>
                        </div>

                        {/* Legal Disclaimer */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-1)'
                            }}>
                                Legal Disclaimer (optional)
                            </label>
                            <input
                                type="text"
                                value={formData.contentGuidelines.legalDisclaimer}
                                onChange={(e) => handleGuidelinesChange('legalDisclaimer', e.target.value)}
                                placeholder="e.g., *While supplies last. Exclusions apply."
                                className="input"
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    {/* Active Toggle */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        marginBottom: 'var(--space-4)'
                    }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => handleChange('isActive', e.target.checked)}
                                style={{ width: '16px', height: '16px' }}
                            />
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                                Active
                            </span>
                        </label>
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                            Inactive promotions won't be included in content generation
                        </span>
                    </div>

                    {/* Info Note */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-3)',
                        background: 'var(--info-50)',
                        borderRadius: 'var(--radius-md)',
                        marginBottom: 'var(--space-4)',
                        border: '1px solid var(--info-200)'
                    }}>
                        <AlertCircle size={16} style={{ color: 'var(--info-600)', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--info-700)' }}>
                            {formData.isOngoing
                                ? 'This ongoing promotion will be available for all content generation until disabled.'
                                : 'This promotion will be used when generating content briefs for dates within the promotion period.'
                            }
                            {' '}Without a promotion, content will never include discount or sale messaging.
                        </span>
                    </div>

                    {/* Footer */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 'var(--space-2)',
                        paddingTop: 'var(--space-3)',
                        borderTop: '1px solid var(--color-border)'
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-ghost"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading && <Loader2 size={16} className="spin" />}
                            {isEditing ? 'Save Changes' : 'Create Promotion'}
                        </button>
                    </div>
                </form>
            </div>
        </div>

        {/* Unsaved Changes Confirmation */}
        <ConfirmationModal
            isOpen={showUnsavedModal}
            onClose={() => setShowUnsavedModal(false)}
            onConfirm={handleSaveAndClose}
            onSecondaryAction={handleDiscardAndClose}
            title="Unsaved Changes"
            message="You have unsaved changes. Would you like to save them before closing?"
            confirmText="Save Changes"
            secondaryActionText="Discard Changes"
            secondaryVariant="danger"
            cancelText="Keep Editing"
            variant="primary"
        />
        </>
    );
};

export default PromotionEditorModal;
